import {
  useAuth,
  useCan,
  useTicker,
  formatDuration,
  formatPhone,
  orchestrator,
  callctl,
  type MonitorMode,
} from "@savvy/runtime";
import { Caps } from "@savvy/shared";
import {
  Button,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
} from "@savvy/ui";
import { useState } from "react";

import { useMonitor } from "../monitor/monitorStore";
import { useLive } from "../state/liveStore";

/** The live floor: agents on top, active calls below, monitor controls inline. */
export function LiveView() {
  return (
    <div>
      <AgentsPanel />
      <CallsPanel />
    </div>
  );
}

const errText = "rounded-md bg-danger-bg px-2.5 py-1.5 text-sm text-danger";

function AgentsPanel() {
  const now = useTicker();
  const agents = useLive((s) => s.agents);
  const callByAgent = useLive((s) => s.callByAgent);
  const partial = useLive((s) => s.partial);
  const api = useAuth((s) => s.api);
  const canManage = useCan(Caps.TelephonyAgentsManage);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const rows = [...agents.values()].sort((a, b) => a.extension.localeCompare(b.extension));

  const refresh = async () => {
    const res = await orchestrator(api).agentStatus();
    useLive.getState().applyAgents(res.agents);
  };

  const setStatus = async (id: string, status: string) => {
    setBusy(id);
    setErr(null);
    try {
      await orchestrator(api).setAgentStatus(id, status);
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  // The switch takes up to a reconcile poll (~2s) to act on the clear, so the
  // countdown may survive one refresh — the event-driven re-read catches it.
  const endWrapUp = async (id: string) => {
    setBusy(id);
    setErr(null);
    try {
      await orchestrator(api).endAgentWrapUp(id);
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="mb-8">
      <h2 className="mb-2 text-base font-semibold">Agents</h2>
      {partial.includes("orchestrator") && (
        <p className={errText}>Agent state could not be read at connect — shown from the last good read.</p>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Extension</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>State</TableHead>
            <TableHead>Missed</TableHead>
            {canManage && <TableHead></TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} className="text-muted-foreground">
                no agents
              </TableCell>
            </TableRow>
          )}
          {rows.map((a) => (
            <TableRow key={a.extension}>
              <TableCell
                className={cn(callByAgent.has(a.extension) && "border-l-2 border-l-st-talking")}
              >
                {a.extension}
                {a.display_name ? ` · ${a.display_name}` : ""}
              </TableCell>
              <TableCell>{a.provisioned ? a.status : "not on the switch"}</TableCell>
              <TableCell>
                {/* Wrap-up is not a status — the queue is skipping them until
                    the window passes. Show it, with the countdown; a manager
                    reading "Available" while a call rings nobody deserves the
                    real reason. */}
                {a.wrap_up_until && Date.parse(a.wrap_up_until) > now ? (
                  <span className="text-warning">
                    wrap-up · {Math.max(0, Math.ceil((Date.parse(a.wrap_up_until) - now) / 1000))}s
                    {canManage && a.id && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="ml-1.5"
                        disabled={busy === a.id}
                        onClick={() => void endWrapUp(a.id!)}
                      >
                        End
                      </Button>
                    )}
                  </span>
                ) : (
                  (a.state ?? "")
                )}
              </TableCell>
              {/* no_answer_count/max is WHY nobody is being offered a call. */}
              <TableCell className={cn(a.no_answer_count >= a.max_no_answer && "text-danger")}>
                {a.no_answer_count} / {a.max_no_answer}
              </TableCell>
              {canManage && (
                <TableCell>
                  {a.id && a.status !== "Available" && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={busy === a.id}
                      onClick={() => void setStatus(a.id!, "Available")}
                    >
                      Set Available
                    </Button>
                  )}
                  {a.id && a.status === "Available" && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={busy === a.id}
                      onClick={() => void setStatus(a.id!, "On Break")}
                    >
                      Set On Break
                    </Button>
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {err && <p className={errText}>{err}</p>}
    </section>
  );
}

function CallsPanel() {
  const now = useTicker();
  const calls = useLive((s) => s.calls);
  const api = useAuth((s) => s.api);
  const session = useMonitor((s) => s.session);
  const pending = useMonitor((s) => s.pending);
  const monitorErr = useMonitor((s) => s.error);

  const canListen = useCan(Caps.TelephonyMonitorListen);
  const canWhisper = useCan(Caps.TelephonyMonitorWhisper);
  const canBarge = useCan(Caps.TelephonyMonitorBarge);
  const canHangup = useCan(Caps.TelephonyCallsHangup);

  const [supervisorExt, setSupervisorExt] = useState(
    () => localStorage.getItem("savvy.supervisor.ext") ?? "",
  );

  // Only caller legs are monitorable rows; the supervisor's own monitor leg is
  // excluded by uuid so it never shows as a bargeable call — the server-side
  // is_monitor_leg flag is the eventual fix, this handles our own session.
  const rows = [...calls.values()]
    .filter((c) => c.uuid !== session?.supervisorUuid)
    .sort((a, b) => b.seenAt - a.seenAt);

  const start = (uuid: string, mode: MonitorMode) => {
    if (!supervisorExt) return;
    localStorage.setItem("savvy.supervisor.ext", supervisorExt);
    void useMonitor.getState().monitor(api, uuid, supervisorExt, mode);
  };

  const modeButton = (uuid: string, mode: MonitorMode, allowed: boolean) => {
    if (!allowed) return null;
    const active = session?.targetUuid === uuid && session.mode === mode;
    return (
      <Button
        variant={active ? "default" : "outline"}
        size="sm"
        disabled={pending || !supervisorExt || active}
        onClick={() => start(uuid, mode)}
      >
        {mode[0]!.toUpperCase() + mode.slice(1)}
      </Button>
    );
  };

  return (
    <section className="mb-8">
      <h2 className="mb-2 text-base font-semibold">Active calls</h2>
      {canListen && (
        <Label className="mb-2 block font-normal">
          My extension{" "}
          <Input
            className="mx-1 inline-block w-20"
            value={supervisorExt}
            onChange={(e) => setSupervisorExt(e.target.value.trim())}
            placeholder="e.g. 1011"
          />
          <span className="text-muted-foreground"> — the softphone that answers when you monitor</span>
        </Label>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>From → To</TableHead>
            <TableHead>Queue / Agent</TableHead>
            <TableHead>State</TableHead>
            <TableHead>Time</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} className="text-muted-foreground">
                no active calls
              </TableCell>
            </TableRow>
          )}
          {rows.map((c) => (
            <TableRow key={c.uuid}>
              <TableCell>
                {formatPhone(c.from)} → {formatPhone(c.to)}
              </TableCell>
              <TableCell>
                {/* Who the call is FOR, then who has it. */}
                {c.clientName && <span className="font-medium">{c.clientName} · </span>}
                {c.queue ?? ""}
                {c.agent ? ` · ${c.agent}` : ""}
              </TableCell>
              <TableCell>{c.transferred ? "transferred" : c.bridged ? "talking" : "waiting"}</TableCell>
              <TableCell className="font-mono tabular-nums">
                {formatDuration(Math.floor((now - c.seenAt) / 1000))}
              </TableCell>
              <TableCell className="space-x-1.5">
                {/* A transferred call is caller↔client — the same privilege
                    reasoning that stops the recording at Complete withholds
                    the monitor ladder here. Hangup stays: the channels are
                    still our switch load. */}
                {!c.transferred && modeButton(c.uuid, "listen", canListen)}
                {!c.transferred && modeButton(c.uuid, "whisper", canWhisper)}
                {!c.transferred && modeButton(c.uuid, "barge", canBarge)}
                {session?.targetUuid === c.uuid && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pending}
                    onClick={() => void useMonitor.getState().stop(api)}
                  >
                    Stop
                  </Button>
                )}
                {canHangup && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pending}
                    onClick={() => void callctl(api).hangup(c.uuid)}
                  >
                    Hangup
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {session && (
        <p className="mt-2 text-sm text-muted-foreground">
          Monitoring <span className="font-mono">{session.targetUuid.slice(0, 8)}</span> as{" "}
          <strong>{session.mode}</strong> — answer the call on your softphone if you have not yet.
        </p>
      )}
      {monitorErr && <p className={errText}>{monitorErr}</p>}
    </section>
  );
}
